import React, { useState, useEffect } from 'react';
import CollapsiblePanel from './CollapsiblePanel';

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function InstallPrompt({ compact = false }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => setInstalled(true);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  let content = null;

  if (installed) {
    if (compact) return null;
    content = <p style={styles.installedText}>✓ Already installed on this device</p>;
  } else if (deferredPrompt) {
    // Android/Chrome - real one-click install
    content = (
      <button style={styles.installBtn} onClick={handleAndroidInstall}>
        Add to Home Screen
      </button>
    );
  } else if (isIOS()) {
    // iOS - no programmatic prompt exists, show instructions
    content = !showIOSInstructions ? (
      <button style={styles.installBtn} onClick={() => setShowIOSInstructions(true)}>
        Add to Home Screen
      </button>
    ) : (
      <div style={styles.iosSteps}>
        <div style={styles.iosStep}>
          1. Tap the <strong>Share</strong> button <span style={styles.shareIcon}>⎋</span> in Safari
        </div>
        <div style={styles.iosStep}>
          2. Scroll down and tap <strong>"Add to Home Screen"</strong>
        </div>
        <div style={styles.iosStep}>
          3. Tap <strong>"Add"</strong> in the top right
        </div>
      </div>
    );
  } else {
    // Neither Android install prompt fired nor iOS detected - likely desktop,
    // where most browsers don't support this kind of install at all
    if (compact) return null;
    content = (
      <p style={styles.desktopText}>
        This works best on your phone. Open this page on your phone's
        browser to add it to your home screen.
      </p>
    );
  }

  if (compact) return content;

  return <CollapsiblePanel title="📲 Install the App">{content}</CollapsiblePanel>;
}

const styles = {
  installBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 1.2rem',
    fontSize: '0.9rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    width: '100%',
  },
  installedText: {
    color: '#00d4aa',
    fontSize: '0.85rem',
    margin: 0,
    fontFamily: "'Syne', sans-serif",
  },
  desktopText: {
    color: '#6b7280',
    fontSize: '0.85rem',
    lineHeight: '1.5',
    margin: 0,
    fontFamily: "'Syne', sans-serif",
  },
  iosSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  iosStep: {
    color: '#d1d5db',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 0.85rem',
  },
  shareIcon: { fontSize: '1rem' },
};
